import { useMemo } from 'react';
import { Source } from 'react-map-gl';
import { useAtomValue } from 'jotai';
import type mapboxgl from 'mapbox-gl';
import { Layer, LayerBand, LAYER_ORDER } from 'components/map/ordered-layer';
import { createMapSourceAtom } from 'components/map/atoms';
import { focusedWellUwiAtom, sectionWellsAtom } from './atoms';

/**
 * The wells a Well-to-Well section can be built from, in the section's own layer rather
 * than the map's vertical-well toggle — off by default on most maps, which left the mode
 * with nothing to click. VERTICAL only, matching the backend's eligibility gate.
 */
const ELIGIBLE_FILTER: mapboxgl.Expression = [
  '==',
  ['get', 'hole_direction'],
  'VERTICAL',
];

export const ELIGIBLE_LAYER_ID = 'cross-section-eligible-wells';
const PICKED_LAYER_ID = 'cross-section-picked-wells';
const LABEL_LAYER_ID = 'cross-section-eligible-well-labels';
const PICKED_LABEL_LAYER_ID = 'cross-section-picked-well-labels';

/**
 * An eligible but unpicked well: cyan from the mock, lightened so a well on the section
 * is the one that reads first. Available, not chosen.
 */
const ELIGIBLE_COLOR = '#7cc7f2';
/** The map label's text colour, and nothing else — the dot itself has no stroke. */
const ELIGIBLE_STROKE = '#003a6d';

/** Picked wells take the section's own colour, which is what the line is drawn in. */
const PICKED_COLOR = '#e83e98';
const MARKER_STROKE = '#161616';

/** The focused well, ringed on top of whatever it already is. */
const FOCUS_COLOR = '#8a3ffc';

/**
 * A picked well's dot. The unpicked one below is a shade smaller as well as lighter:
 * size and weight both say "chosen", and either alone was too quiet to read at a glance.
 */
const PICKED_RADIUS: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['zoom'],
  6,
  3,
  11,
  5,
  14,
  6,
];

/** Enough smaller to notice beside a picked well, not so much it stops being clickable. */
const ELIGIBLE_RADIUS: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['zoom'],
  6,
  2.5,
  11,
  4,
  14,
  4.5,
];

const LABEL_SIZE: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['zoom'],
  10,
  9,
  14,
  11,
];

/**
 * From which zoom the section offers wells, and from which it names the ones it is
 * only offering — both above the map's own wells, which appear at ten, the `wells-v2`
 * tileset's floor.
 *
 * A well already ON the section is held back by neither: it is drawn at every zoom and
 * named at every zoom. Product asked for that plainly — the wells the section is made
 * of are what the view is about, and a name that goes away when you pull back to see
 * the whole traverse is a name missing exactly when it is wanted. So the offer costs
 * more zoom than the section itself, not less.
 */
export const ELIGIBLE_MIN_ZOOM = 12;
const ELIGIBLE_LABEL_MIN_ZOOM = 14;

/**
 * Where a well's name sits, in ems of its own text — below an unpicked well, and ABOVE
 * a picked one, which is where UAT asked for it. Negative is up, and the anchor flips
 * with it so the offset always measures to the edge nearest the dot.
 *
 * A picked well needs the larger figure either way: its numbered badge runs to ~11.5px
 * and the badge's own number is drawn with `text-ignore-placement` — it has to be, or a
 * dropped number misnumbers the traverse — so Mapbox's collision engine cannot see it
 * and route the name around it. The clearance is ours to give.
 */
const LABEL_OFFSET_EM = 0.9;
const PICKED_LABEL_OFFSET_EM = -1.4;

/**
 * What a well's label says, shared by both label layers: name over uwi, the way the
 * mock labels a well and the way the bore is labelled on the plot — what it is called,
 * then which one it is. The uwi is set smaller so the name still reads first.
 */
const labelText = {
  'text-field': [
    'format',
    ['concat', ['get', 'lease_name'], ' ', ['get', 'well_number']],
    {},
    '\n',
    {},
    ['get', 'uwi'],
    { 'font-scale': 0.85 },
  ] as unknown as string,
  'text-size': LABEL_SIZE,
};

/** One look for both, so a well reads the same whether it is on the section or offered. */
const LABEL_PAINT = {
  'text-color': ELIGIBLE_STROKE,
  'text-halo-color': '#ffffff',
  'text-halo-width': 1.5,
};

/**
 * Eligible wells for Well to Well, and which of them are on the section.
 *
 * Only mounted in that mode: in Arbitrary Line a click is a vertex, so drawing the
 * wells a click cannot pick would promise a gesture the map does not honour.
 *
 * @return {JSX.Element | null} The wells, or null before the source resolves.
 */
export const CrossSectionWellsLayer = (): JSX.Element | null => {
  const sourceAtom = useMemo(() => createMapSourceAtom('wellsV2'), []);
  const source = useAtomValue(sourceAtom);
  const picked = useAtomValue(sectionWellsAtom);
  const focusedWellUwi = useAtomValue(focusedWellUwiAtom);

  if (!source) {
    return null;
  }

  const pickedUwis = picked.map((well) => well.uwi);

  return (
    // Under the section's own line and vertices, which are drawn over everything.
    <LayerBand band={LAYER_ORDER.wells}>
      <Source id="cross-section-wells" type="vector" url={source}>
        <Layer
          id={ELIGIBLE_LAYER_ID}
          type="circle"
          source-layer="surface-hole"
          filter={ELIGIBLE_FILTER}
          minzoom={ELIGIBLE_MIN_ZOOM}
          paint={{
            'circle-radius': ELIGIBLE_RADIUS,
            'circle-color': ELIGIBLE_COLOR,
            'circle-stroke-width': 0,
          }}
        />
        {/* Wells on the section, styled distinctly from eligible but unselected —
            the numbered vertex dot lands on top of this, and its ring stays
            visible under the number. */}
        {pickedUwis.length > 0 && (
          <Layer
            id={PICKED_LAYER_ID}
            type="circle"
            source-layer="surface-hole"
            filter={[
              'all',
              ELIGIBLE_FILTER,
              ['in', ['get', 'uwi'], ['literal', pickedUwis]],
            ]}
            paint={{
              'circle-radius': PICKED_RADIUS,
              'circle-color': PICKED_COLOR,
              'circle-stroke-color': MARKER_STROKE,
              'circle-stroke-width': 1.5,
            }}
          />
        )}
        {focusedWellUwi !== null && (
          <Layer
            id="cross-section-focused-eligible-well"
            type="circle"
            source-layer="surface-hole"
            filter={['==', ['get', 'uwi'], focusedWellUwi]}
            paint={{
              // The focus ring reads against a picked well, so it takes that size.
              'circle-radius': PICKED_RADIUS,
              'circle-color': 'rgba(0, 0, 0, 0)',
              'circle-stroke-color': FOCUS_COLOR,
              'circle-stroke-width': 2.5,
            }}
          />
        )}
        {/* The names of the wells the section is made of: at every zoom, and never
    dropped. Few enough to overlap without turning the map to soup, and the
    traverse is unreadable without them. */}
        {pickedUwis.length > 0 && (
          <Layer
            id={PICKED_LABEL_LAYER_ID}
            type="symbol"
            source-layer="surface-hole"
            filter={[
              'all',
              ELIGIBLE_FILTER,
              ['in', ['get', 'uwi'], ['literal', pickedUwis]],
            ]}
            layout={{
              ...labelText,
              // Above the badge, which is where UAT asked for it. Anchored by its foot
              // so the offset measures the gap rather than the distance to its middle.
              'text-offset': [0, PICKED_LABEL_OFFSET_EM],
              'text-anchor': 'bottom',
              'text-allow-overlap': true,
            }}
            paint={LABEL_PAINT}
          />
        )}
        {/* And the names of the wells it is merely offering, which cost more zoom and
    yield rather than overprint — to each other, and to the picked names above,
    which are drawn regardless. */}
        <Layer
          id={LABEL_LAYER_ID}
          type="symbol"
          source-layer="surface-hole"
          filter={[
            'all',
            ELIGIBLE_FILTER,
            ['!', ['in', ['get', 'uwi'], ['literal', pickedUwis]]],
          ]}
          minzoom={ELIGIBLE_LABEL_MIN_ZOOM}
          layout={{
            ...labelText,
            // Below the dot, by its head, so a picked well's name and an unpicked
            // one's never reach for the same space.
            'text-offset': [0, LABEL_OFFSET_EM],
            'text-anchor': 'top',
            'text-allow-overlap': false,
          }}
          paint={LABEL_PAINT}
        />
      </Source>
    </LayerBand>
  );
};
